/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import { Member, MemberProfileData, MemberTitle } from 'src/modules/member/models/member';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { getMembers } from 'src/shared/services/memberService';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { Clear, Email, Phone, Send, WarningAmber, Person, GroupAdd, Groups, ManageAccounts, PostAdd, AccountBox } from '@mui/icons-material';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { Button, Box, Divider, IconButton, Typography, Tooltip, Icon, Link } from '@mui/material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import dayjs from 'dayjs';
import { County } from 'src/shared/models/county';
import { useMemberContext } from 'src/shared/contexts/memberContext';
import { District } from 'src/shared/models/district/district';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { TextOverflowHidden } from 'src/shared/components/atoms/textOverflowHidden';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { EmptyGridCell } from 'src/shared/components/atoms/emptyGridCell';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberStatuses } from './memberStatuses';
import { MemberSearchFilter } from '../models/memberSearchFilter';
import { useExtraMembershipLabel } from '../hooks/useExtraMembershipLabel';
import { MemberSearchFormState } from '../models/memberSearchFormState';
import { MemberTitleBadge } from './memberTitleBadge';
import { MemberDetailModal } from './memberDetailModal';
import { MemberGridCustomCol } from '../models/memberGridCustomCol';

type MemberDataGridProps = {
  otherContactMode: boolean;
  searchFilter: MemberSearchFormState;
  searchFilterCallback: (filter: MemberSearchFormState) => void;
  gridColumns?: GridColDef[] | undefined;
  loadingCallback?: (loading: boolean) => void;
  checkboxSelection: boolean;
  disableRowClick?: boolean;
};

export const getMemberSearchFilter = (searchFilter: MemberSearchFormState, otherContactMode: boolean) => {
  const searchString = searchFilter.searchString ? searchFilter.searchString.trim() : '';

  // Föreningar (associationIds) kan komma både från distrikt-väljaren (icke-parent) och från den
  // fasta lokalförenings-väljaren (grupp A). Slå ihop och deduplicera.
  const associationIdsFromDistrict = Array.isArray(searchFilter.districtIds)
    ? (searchFilter.districtIds as District[])
        .filter((x) => !x.isParent)
        .map((x) => x.code)
        .map(Number)
    : [];

  const associationIdsFromLocalAss = Array.isArray(searchFilter.associationIds)
    ? (searchFilter.associationIds as District[])
        .filter((x) => !x.isParent)
        .map((x) => x.code)
        .map(Number)
    : [];

  const associationIds = Array.from(new Set([...associationIdsFromDistrict, ...associationIdsFromLocalAss]));

  const districtIds = Array.isArray(searchFilter.districtIds)
    ? (searchFilter.districtIds as District[])
        .filter((x) => x.isParent)
        .map((x) => x.code)
        .map(Number)
    : [];

  const countyCodes = Array.isArray(searchFilter.countyCode)
    ? (searchFilter.countyCode as County[])
        .filter((x) => x.isParent)
        .map((x) => x.code)
        .join(',')
    : '';

  const municipalityCodes = Array.isArray(searchFilter.countyCode)
    ? (searchFilter.countyCode as County[])
        .filter((x) => !x.isParent)
        .map((x) => x.code)
        .join(',')
    : '';

  const profileItemIds = Array.isArray(searchFilter.profileItemIds)
    ? (searchFilter.profileItemIds as MemberProfileData[])
        .map((x) => x.memberProfileItem_id)
        .map(Number)
        .filter((item, pos, self) => {
          return self.indexOf(item) === pos;
        })
    : [];

  const excludedProfileItemIds = Array.isArray(searchFilter.excludedProfileItemIds)
    ? (searchFilter.excludedProfileItemIds as MemberProfileData[])
        .map((x) => x.memberProfileItem_id)
        .map(Number)
        .filter((item, pos, self) => {
          return self.indexOf(item) === pos;
        })
    : [];

  const statuses = Array.isArray(searchFilter.status)
    ? searchFilter.status
        .map((x) => x.values)
        .map(Number)
        .filter((x) => !(otherContactMode && x !== MemberStatusEnum.NewMember)) // Remove newMember if otherContactMode is active
        .filter((item, pos, self) => {
          return self.indexOf(item) === pos;
        })
    : [];

  const checkDate = (dt) => (dt && dayjs(dt).isValid() ? dayjs(dt).format('YYYYMMDD') : '');

  const admDateStart = checkDate(searchFilter.admDateStart);
  const admDateEnd = checkDate(searchFilter.admDateEnd);
  const resDateStart = checkDate(searchFilter.resDateStart);
  const resDateEnd = checkDate(searchFilter.resDateEnd);
  const dueDateStart = checkDate(searchFilter.dueDateStart);
  const dueDateEnd = checkDate(searchFilter.dueDateEnd);
  const payDateStart = checkDate(searchFilter.payDateStart);
  const payDateEnd = checkDate(searchFilter.payDateEnd);
  const birthDateStart = checkDate(searchFilter.birthDateStart);
  const birthDateEnd = checkDate(searchFilter.birthDateEnd);

  const checkBoolean = (c) => (c === 'true' || c === 'false' ? c : '');

  const hasMobilePhone = checkBoolean(searchFilter.hasMobilePhone);
  const hasEmailAddress = checkBoolean(searchFilter.hasEmailAddress);
  const hasNewspaper = checkBoolean(searchFilter.hasNewspaper);
  const isMainMember = checkBoolean(searchFilter.isMainMember);
  const isFamilyMember = checkBoolean(searchFilter.isFamilyMember);
  const isExtraMember = checkBoolean(searchFilter.isExtraMember);
  const hasExtraMemberships = checkBoolean(searchFilter.hasExtraMemberships);
  const otherContact = checkBoolean(otherContactMode ? 'true' : searchFilter.otherContact);
  const includeResignedTitles = searchFilter.includeResignedTitles ?? '';

  const resCause = searchFilter.resCause ? searchFilter.resCause.value : '';
  const sex = searchFilter.sex ? searchFilter.sex.value : '';

  const titleIds = Array.isArray(searchFilter.titleIds) ? searchFilter.titleIds.map((x) => x.code) : [];

  const trimOrEmpty = (v: string | null | undefined) => (typeof v === 'string' ? v.trim() : '');

  const searchParams: MemberSearchFilter = {
    associationIds,
    districtIds,
    postCode: searchFilter.postCode,
    firstName: trimOrEmpty(searchFilter.firstName),
    lastName: trimOrEmpty(searchFilter.lastName),
    pnr: trimOrEmpty(searchFilter.pnr),
    co: trimOrEmpty(searchFilter.co),
    country: trimOrEmpty(searchFilter.country),
    email: trimOrEmpty(searchFilter.email),
    phone: trimOrEmpty(searchFilter.phone),
    phoneOther: trimOrEmpty(searchFilter.phoneOther),
    address: trimOrEmpty(searchFilter.address),
    addressOther: trimOrEmpty(searchFilter.addressOther),
    city: trimOrEmpty(searchFilter.city),
    custom1: trimOrEmpty(searchFilter.custom1),
    custom2: trimOrEmpty(searchFilter.custom2),
    custom3: trimOrEmpty(searchFilter.custom3),
    custom4: trimOrEmpty(searchFilter.custom4),
    custom5: trimOrEmpty(searchFilter.custom5),
    custom6: trimOrEmpty(searchFilter.custom6),
    custom7: trimOrEmpty(searchFilter.custom7),
    custom8: trimOrEmpty(searchFilter.custom8),
    custom9: trimOrEmpty(searchFilter.custom9),
    custom10: trimOrEmpty(searchFilter.custom10),
    newspaper: trimOrEmpty(searchFilter.newspaper),
    searchString,
    countyCode: countyCodes,
    municipalityCode: municipalityCodes,
    profileItemIds,
    excludedProfileItemIds,
    paymentMethods: Array.isArray(searchFilter.paymentMethods) ? searchFilter.paymentMethods : [],
    status: statuses,
    admDateStart,
    admDateEnd,
    resDateStart,
    resDateEnd,
    dueDateStart,
    dueDateEnd,
    payDateStart,
    payDateEnd,
    birthDateStart,
    birthDateEnd,
    hasMobilePhone,
    hasEmailAddress,
    hasNewspaper,
    isMainMember,
    isFamilyMember,
    isExtraMember,
    hasExtraMemberships,
    otherContact,
    resCause,
    sex,
    titleIds,
    includeResignedTitles,
  };

  return searchParams;
};

export const MemberDataGrid = (props: MemberDataGridProps) => {
  const {
    otherContactMode,
    searchFilter,
    searchFilterCallback,
    gridColumns = undefined,
    loadingCallback = undefined,
    disableRowClick = false,
    checkboxSelection,
  } = props;

  const [showTitlesColumn, setShowTitlesColumn] = useState<boolean>(false);
  const { t } = useTranslation(['member', 'common']);
  const { currentUser } = useGlobals();
  const { setComposeEmailMode, setCreateInvoiceMode, setSelectedMembers } = useMemberContext();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { scrollIntoView } = useScrollIntoView();
  const friendLabel = useExtraMembershipLabel();

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const [selectedMemberId, setSelectedMemberId] = useState<number>();
  const [currentMemberIds, setCurrentMemberIds] = useState<number[]>();

  const [allRows, setAllRows] = useState<Member[]>([]); // Spar undan en array med data för alla sidor som hämtats för att kunna filtrera selected mot denna
  const [rows, setRows] = useState<Member[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => {
    setData((prev) => ({ ...prev, [k]: v }));
  };

  useEffect(() => {
    if (data.pageSize !== searchFilter.pageSize || data.page !== searchFilter.pageIndex) {
      setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
    }
  }, [data.page, data.pageSize, searchFilter.pageIndex, searchFilter.pageSize]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const uniqueById = (items: Array<Member>) => {
    const set = new Set();
    return items.filter((item) => {
      const isDuplicate = set.has(item.member_id);
      set.add(item.member_id);
      return !isDuplicate;
    });
  };

  const fetchData = useCallback(async () => {
    const searchParams = getMemberSearchFilter(searchFilter, otherContactMode);
    updateData('loading', true);
    if (loadingCallback) loadingCallback(true);
    try {
      const result = await getMembers(
        searchFilter.pageIndex,
        searchFilter.pageSize,
        searchFilter.sortCol,
        searchFilter.sortDir,
        searchParams,
        [],
        '',
      );
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        const memberResult = extendObjectWithId(result.data.results, 'member_id');
        setTimeout(() => {
          // setSelectionModel(() => memberResult.map((r) => r.id));
          setCurrentMemberIds(memberResult.map((x) => x.id));
          setRows(memberResult);
          setAllRows((prev) => {
            return uniqueById([...prev, ...memberResult]);
          });
          setShowTitlesColumn((searchFilter && searchFilter.titleIds && searchFilter.titleIds?.length > 0) || !!searchFilter.includeResignedTitles);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error ?? 'Unkown error: getNewsListPageDetails');
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        if (loadingCallback) loadingCallback(false);
      }
    }
  }, [isMounted, otherContactMode, searchFilter, loadingCallback]);

  useEffect(() => {
    if (isMounted() && currentUser) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted, currentUser]);

  const onRowClick = (params) => {
    if (otherContactMode) {
      navigate(`${RoutesEnum.MemberOtherContactDetails}/${params.row.id}`);
    } else {
      navigate(`${RoutesEnum.MemberDetails}/${params.row.id}`);
    }
  };

  const memberEmailClicked = (members: Member[]) => {
    setSelectedMembers(members);
    setComposeEmailMode(true);
  };

  const handleCreateInvoice = (members: Member[]) => {
    setSelectedMembers(members);
    setCreateInvoiceMode(true);
  };

  const statuses = searchFilter.status ?? [];
  const keys = statuses.map((s) => s.key);

  const onlyNewMember = keys.length === 1 && keys[0] === MemberStatusTabKeysEnum.NewMember;

  const onlyPaidOrNotPaid = keys.length > 0 && keys.every((k) => k === MemberStatusTabKeysEnum.Paid || k === MemberStatusTabKeysEnum.NotPaid);

  const showCreatedDate = onlyNewMember;
  const showAdmDate = onlyPaidOrNotPaid;

  const columns: GridColDef[] = gridColumns ?? [
    {
      field: 'memberNr',
      headerName: otherContactMode ? t('member:tableHeaders.nr') : t('member:tableHeaders.member_id'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const { memberNr } = params.row;
        return <>{memberNr || <EmptyGridCell />}</>;
      },
    },
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.firstName'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { isFamilyMember, isMainMember, isExtraMember, hasExtraMemberships } = params.row;
        return (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
            <Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexGrow: 1 }}>
              {params.row.firstName || ''}
            </Box>
            {isFamilyMember && !isMainMember && (
              <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                <Person fontSize='small' sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
            {isMainMember && (
              <Tooltip title={t('member:memberDetails.headMember')} arrow>
                <ManageAccounts fontSize='small' sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
            {isExtraMember && (
              <Tooltip title={t('member:extraMembership.isExtraMember', { label: friendLabel })} arrow>
                <Groups fontSize='small' sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
            {hasExtraMemberships && !isExtraMember && (
              <Tooltip title={t('member:extraMembership.hasExtraMemberships', { label: friendLabel.toLowerCase() })} arrow>
                <GroupAdd fontSize='small' sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'lastName',
      headerName: otherContactMode ? t('member:tableHeaders.name') : t('member:tableHeaders.lastName'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        const { firstName, lastName } = params.row;
        if (otherContactMode)
          return (
            <>
              {firstName || ''} {lastName || ''}
            </>
          );
        return <>{lastName || ''}</>;
      },
    },
    {
      field: 'district',
      headerName: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district')),
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return params.row.district?.countyAss && <TextOverflowHidden>{params.row.district?.countyAss}</TextOverflowHidden>;
      },
    },
    {
      field: 'association',
      headerName: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association')),
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return params.row.association?.localAss && <TextOverflowHidden>{params.row.association?.localAss}</TextOverflowHidden>;
      },
    },
    ...(!otherContactMode
      ? [
          {
            field: 'status',
            headerName: t('member:tableHeaders.memberStatus'),
            sortable: false,
            flex: 0.75,
            renderCell: (params: GridCellParams) => {
              const { status: stat, resDate } = params.row;
              return <MemberStatuses status={stat} resDate={resDate} chipSize='small' />;
            },
          },
        ]
      : []),
    ...(showAdmDate && !otherContactMode
      ? [
          {
            field: 'admDate',
            headerName: t('member:admDate'),
            flex: 0.7,
            renderCell: (params: GridCellParams<Member>) => {
              const value = params.row.admDate;
              const text = value ? String(value) : '';
              return text ? <TextOverflowHidden>{DateHelper.getDate(text)}</TextOverflowHidden> : <EmptyGridCell />;
            },
          },
        ]
      : []),
    ...(showCreatedDate
      ? [
          {
            field: 'createDate',
            headerName: t('member:createdDate'),
            flex: 0.7,
            renderCell: (params: GridCellParams<Member>) => {
              const value = params.row.createDate;
              const text = value ? String(value) : '';
              return text ? <TextOverflowHidden>{DateHelper.getDate(text)}</TextOverflowHidden> : <EmptyGridCell />;
            },
          },
        ]
      : []),
    {
      field: 'member_id',
      headerName: '',
      sortable: false,
      width: 105, // 140
      renderCell: (params: GridCellParams) => {
        const { updateRequest, email, member_id, phone, phoneOther } = params.row;
        const DisplayEmptyIcon = () => (
          <IconButton disabled size='small'>
            <Icon fontSize='small' />
          </IconButton>
        );
        const showPhone = phone || phoneOther;

        return (
          <Box
            sx={{
              display: 'flex',
              '& > *:not(:last-child)::after': {
                content: '" "',
                position: 'absolute',
                backgroundColor: '#eee',
                width: '1px',
                top: '0.5rem',
                bottom: '0.5rem',
                right: 0,
                opacity: 1,
              },
              '& .MuiButtonBase-root': {
                width: '2rem',
              },
            }}
          >
            {updateRequest ? (
              <Tooltip describeChild title={`${t('member:memberDetails.updateRequestedHeader')}`} arrow>
                <IconButton>
                  <WarningAmber fontSize='small' color='warning' />
                </IconButton>
              </Tooltip>
            ) : (
              <DisplayEmptyIcon />
            )}
            {email ? (
              <Tooltip describeChild title={`${email}`} arrow>
                <IconButton
                  component={Link as any}
                  to={`mailto: ${email}`}
                  onClick={
                    memberEmailClicked !== undefined
                      ? (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          memberEmailClicked(rows.filter((x) => x.member_id === member_id));
                        }
                      : undefined
                  }
                  style={{ display: 'flex', color: Colors.LinkColor }}
                  size='small'
                >
                  <Email fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : (
              <DisplayEmptyIcon />
            )}
            {showPhone ? (
              <Tooltip describeChild title={`${[phone, phoneOther].filter((x) => x !== '').join(', ')}`} arrow>
                <IconButton component={Link as any} to={`tel: ${showPhone}`} target='_top' style={{ display: 'flex', color: Colors.LinkColor }}>
                  <Phone fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : (
              <DisplayEmptyIcon />
            )}
          </Box>
        );
      },
    },
    {
      field: 'memberGuid',
      headerName: '',
      sortable: false,
      width: 40,
      renderCell: (params: GridCellParams) => {
        const { member_id } = params.row;
        return (
          <IconButton
            color='primary'
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setSelectedMemberId(member_id);
            }}
            sx={{ color: (thm) => thm.palette.grey[400], '&:hover': { color: (thm) => thm.palette.primary.main } }}
          >
            <AccountBox />
          </IconButton>
        );
      },
    },
  ];

  if (showTitlesColumn) {
    columns.push({
      field: 'titleIds',
      headerName: t('member:tableHeaders.memberTitle'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { titles } = params.row;
        return (
          <Box sx={{ p: 0.5 }}>
            <Box sx={{ lineHeight: 'normal', display: 'flex', flexWrap: 'wrap', mb: -0.5 }}>
              {titles
                .sort((a: MemberTitle, b: MemberTitle) => (a.resdate < b.resdate ? -1 : 1))
                .map((item) => {
                  return <MemberTitleBadge memberTitle={item} />;
                })}
            </Box>
          </Box>
        );
      },
    });
  }

  // Add custom profile columns for normal grid, not otherContactMode grid
  if (!otherContactMode) {
    const memberGridCustomCols = JSON.parse(
      getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.MemberGridCustomCols, '[]'),
    ) as MemberGridCustomCol[];
    memberGridCustomCols.forEach((item) => {
      columns.splice(item.index, 0, {
        field: item.name,
        headerName: item.heading,
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          return <TextOverflowHidden>{params.row[item.name]}</TextOverflowHidden>;
        },
      });
    });
  }

  return (
    <Box
      sx={{
        '& .MuiDataGrid-cell': {
          cursor: 'pointer',
        },
      }}
      // ref={scrollRef}
    >
      <DataGrid<Member>
        autoHeight
        columnVisibilityModel={{
          // memberNr: !otherContactMode,
          firstName: !otherContactMode,
          district: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.MemberGridHideDistrict, 'false').toLowerCase() === 'false',
          association: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.MemberGridHideAssociation, 'false').toLowerCase() === 'false',
        }}
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel, newSortModel.length, sortModel);
          const currentSortModel = sortModel.length ? sortModel[0] : newSortModel[0];
          const sort = currentSortModel.sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }

          setSortModel(newSortModel);
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        onRowClick={disableRowClick ? undefined : onRowClick}
        selectionModel={selectionModel}
        onPageChange={(p) => onPageChange(p)}
        onPageSizeChange={(p) => onPageSizeChange(p)}
        disablePointer={disableRowClick}
      />
      {selectedMemberId && currentMemberIds && (
        <MemberDetailModal
          otherContactMode={otherContactMode}
          currentMemberIds={currentMemberIds}
          changeMemberId={(id) => setSelectedMemberId(id)}
          memberId={selectedMemberId}
          onClose={() => setSelectedMemberId(undefined)}
        />
      )}
      {checkboxSelection && selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }}>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button
              variant='contained'
              disableElevation
              endIcon={<Send fontSize='small' />}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const membersFromSelection = allRows.filter((row) => selectedIDs.has(row.member_id || 0));
                if (membersFromSelection && memberEmailClicked !== undefined) memberEmailClicked(membersFromSelection);
              }}
              sx={{ mr: 1 }}
            >
              {t('member:footer.sendMessage')}
            </Button>
            {!otherContactMode && (
              <Button
                variant='contained'
                disableElevation
                endIcon={<PostAdd fontSize='small' />}
                onClick={() => {
                  const selectedIDs = new Set(selectionModel);
                  const membersFromSelection = allRows.filter((row) => selectedIDs.has(row.member_id || 0));
                  if (membersFromSelection) handleCreateInvoice(membersFromSelection);
                }}
                sx={{ mr: 1 }}
              >
                {t('member:footer.createInvoice')}
              </Button>
            )}
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};
